import { Fragment, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { SentinelMessageMetrics } from '@/types/sentinelAnalytics';
import { AnalyticsTable, Cell, EdgeCell, EdgeHead, ExpandToggle, Head, STICKY_CELL, STICKY_HEAD, SUB_ROW_NAME } from './analytics-table';
import { subRowProps, useExpandedRows } from './use-expanded-rows';
import { useSortedRows } from './use-sorted-rows';
import { formatPercent } from './format';
import { Legend } from './legend';
import { MiniSplit } from './mini-split';
import { RateCell } from './rate-cell';

type SortKey = 'name' | 'effort' | 'corrected' | 'coverage' | 'failure';
/** One table row: a category or a channel, with a category's sub-categories as `subRows`. */
type Row = { name: string; messages: SentinelMessageMetrics; subRows?: Row[] };

function sortValue({ name, messages }: Row, key: SortKey): string | number | null {
  if (key === 'name') return name;
  if (key === 'effort') return messages.effort.human + messages.effort.ai === 0 ? null : messages.effort.human / (messages.effort.human + messages.effort.ai);
  if (key === 'corrected') return messages.correctedResponses.rate;
  return key === 'coverage' ? messages.aiCoverage.rate : messages.aiGenerationFailure.rate;
}

interface MessageMetricsTableProps {
  /** First column header, e.g. "Category Name". */
  nameLabel: string;
  rows: Row[];
}

function MessageRow({ row: { name, messages }, name: label, sub = false, closing = false }: { row: Row; name: ReactNode; sub?: boolean; closing?: boolean }) {
  return (
    <tr {...(sub ? subRowProps(closing) : {})}>
      <EdgeCell sticky />
      <Cell divider={false} className={cn(STICKY_CELL, 'border-r border-r-gray-500 font-medium', sub && SUB_ROW_NAME)}>
        <span className='flex items-center gap-1.5'>{label}</span>
      </Cell>
      <Cell divider={false} className='pl-3'>
        <MiniSplit left={messages.effort.human} right={messages.effort.ai} label={`${name}: human vs AI effort`} className='w-full min-w-[213px]' />
      </Cell>
      <Cell className='tabular-nums'>{formatPercent(messages.correctedResponses.rate)}</Cell>
      <Cell><RateCell metric={messages.aiCoverage} /></Cell>
      <Cell><RateCell metric={messages.aiGenerationFailure} /></Cell>
      <EdgeCell />
    </tr>
  );
}

export function MessageMetricsTable({ nameLabel, rows }: MessageMetricsTableProps) {
  const { sorted, sortRows, sortProps } = useSortedRows(rows, sortValue);
  const { isExpanded, isClosing, toggle } = useExpandedRows();
  return (
    <AnalyticsTable>
      <thead>
        <tr>
          <EdgeHead sticky />
          <Head divider={false} className={cn(STICKY_HEAD, 'w-60 border-r border-r-gray-500 py-[11px]')} sort={sortProps('name')}>{nameLabel}</Head>
          <Head divider={false} className='py-[11px] pl-3' sort={sortProps('effort')}>
            <span className='flex items-center gap-12'>
              Effort Ratio
              <Legend items={[{ label: 'Human', swatch: 'bg-indigo-700' }, { label: 'AI', swatch: 'bg-indigo-200' }]} />
            </span>
          </Head>
          <Head className='py-[11px]' sort={sortProps('corrected')}>Corrected Responses</Head>
          <Head className='py-[11px]' sort={sortProps('coverage')}>AI Coverage</Head>
          <Head className='py-[11px]' sort={sortProps('failure')}>AI Generation Failure</Head>
          <EdgeHead />
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <Fragment key={row.name}>
            <MessageRow row={row} name={row.subRows?.length ? <ExpandToggle name={row.name} expanded={isExpanded(row.name)} onToggle={() => toggle(row.name)} /> : row.name} />
            {row.subRows && (isExpanded(row.name) || isClosing(row.name)) ? sortRows(row.subRows).map((sub) => <MessageRow key={`${row.name}/${sub.name}`} row={sub} name={sub.name} sub closing={isClosing(row.name)} />) : null}
          </Fragment>
        ))}
      </tbody>
    </AnalyticsTable>
  );
}
